/**
 * SA-1387 (spec §5.4): the Overview tab. Connection (read-only facts), what
 * the mailbox does with a way into Routing, earlier mail with its progress
 * and its two controls, and outbound for an email mailbox.
 */
import type { ReactNode } from 'react'
import { History } from 'lucide-react'
import { Button } from '@/components/ui'
import { FactList, type Fact } from '@/components/records/FactList'
import type { Mailbox } from '@/services/mailboxes'
import { backloadLine, backloadWords } from './backloadText'
import { accountLine } from './connectedAccountText'
import { groupLine, isGroupSource } from './groupSourceText'
import { healthLine } from './healthText'
import { EDIT_ROUTING, NONE, OVERVIEW_WORDS, RECEIVING_NORMALLY, USE_A_ROUTER } from './mailboxesText'

interface MailboxOverviewProps {
  mailbox: Mailbox
  /** `sinkLabel(...)`: what the mailbox does. */
  setup: string
  personaName: string | null
  ownerName: string | null
  onEditRouting: () => void
  onUseRouter: () => void
  onLoadEarlier: () => void
  onCancelLoad: () => void
  onToggleOutbound: (on: boolean) => void
}

/** The section recipe (SA-1059) without a toggle: nothing here is remembered or folded. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-section-border bg-card shadow-xs">
      <h3 className="border-b border-border bg-muted/40 px-4 py-2 text-[12.5px] font-semibold text-foreground">{title}</h3>
      <div className="space-y-2 px-4 py-3">{children}</div>
    </section>
  )
}

export function MailboxOverview({
  mailbox: mb, setup, personaName, ownerName,
  onEditRouting, onUseRouter, onLoadEarlier, onCancelLoad, onToggleOutbound,
}: MailboxOverviewProps) {
  const group = isGroupSource(mb)
  const health = healthLine(mb, new Date())
  const words = backloadWords(mb.channel)
  const canBackload = mb.status === 'active' || mb.status === 'paused'
  const loadingEarlier = mb.backload?.status === 'loading'

  const facts: Fact[] = [
    { key: 'account', label: OVERVIEW_WORDS.account, value: group ? groupLine(mb) : accountLine(mb).text },
  ]
  if (health) {
    facts.push({
      key: 'health',
      label: OVERVIEW_WORDS.health,
      value: <span data-testid="health-line" title={mb.last_error ?? undefined}>{health}</span>,
    })
  } else if (mb.status === 'active') {
    facts.push({ key: 'health', label: OVERVIEW_WORDS.health, value: RECEIVING_NORMALLY })
  }
  facts.push(
    { key: 'persona', label: OVERVIEW_WORDS.persona, value: personaName ?? NONE },
    { key: 'owner', label: OVERVIEW_WORDS.owner, value: ownerName ?? NONE },
  )

  return (
    <div className="space-y-4">
      <Section title={OVERVIEW_WORDS.connection}>
        <FactList facts={facts} />
      </Section>

      <Section title={OVERVIEW_WORDS.whatItDoes}>
        <p className="text-sm text-foreground">{setup}</p>
        <Button variant="outline" size="sm" onClick={mb.sink_type === 'router' ? onEditRouting : onUseRouter}>
          {mb.sink_type === 'router' ? EDIT_ROUTING : USE_A_ROUTER}
        </Button>
      </Section>

      {(mb.backload || canBackload) && (
        <Section title={words.earlier}>
          {mb.backload && (
            <p className="text-sm text-muted-foreground" data-testid="backload-line">
              {backloadLine(mb.backload, mb.status, words)}
            </p>
          )}
          {(canBackload || loadingEarlier) && (
            <div className="flex flex-wrap items-center gap-3">
              {loadingEarlier && mb.backload && (
                // SA-1079: the bar says what the line says; max is never 0
                // (an indeterminate bar on a run born with nothing to load).
                <progress className="h-1.5 w-56 max-w-full" value={mb.backload.position}
                  max={Math.max(1, mb.backload.total)} aria-label={words.progressLabel} />
              )}
              {canBackload && !loadingEarlier && (
                <Button variant="outline" size="sm" onClick={onLoadEarlier} leftIcon={<History className="w-4 h-4" />}>
                  {words.title}
                </Button>
              )}
              {loadingEarlier && (
                <Button variant="ghost" size="sm" onClick={onCancelLoad}>
                  {words.cancel}
                </Button>
              )}
            </div>
          )}
        </Section>
      )}

      {!group && (
        <Section title={OVERVIEW_WORDS.outbound}>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={mb.outbound_enabled}
              onChange={(e) => onToggleOutbound(e.target.checked)}
              className="h-4 w-4"
            />
            Outbound (AI-drafted replies)
          </label>
          <p className="text-xs text-muted-foreground">
            Adds the provider&apos;s send scope at the next Connect. Staff review and
            send every draft — nothing sends automatically.
          </p>
        </Section>
      )}
    </div>
  )
}
